/**
 * Renders a guide's content blocks.
 *
 * One component for all 126 guides, so the guide chrome — the box styling, the callout,
 * the related-links list, the FAQ — is defined once instead of copy-pasted into every
 * page. The markup follows the "Guide pages (prose content)" pattern in CLAUDE.md: a
 * `prose` container, with the boxed sections opted out of it via `not-prose`.
 *
 * Every string arrives as inline markup (`**bold**`, `*italic*`, `` `code` ``, `^{1}`,
 * `[label](/href)`) and goes through `parseInline`. Nothing here calls
 * `dangerouslySetInnerHTML` — the parser returns data and React renders it, so a guide
 * cannot inject markup by writing it into a content file.
 *
 * The `faq` block renders the visible list only. Its `FAQPage` structured data is
 * emitted once per page by the route, from the same items — see `guideFaqItems`.
 */

import { FaqList } from "@/components/faq-list";
import Link from "@/components/locale-link";
import type {
  GuideBlock,
  GuideExampleRow,
  GuideListItem,
} from "@/lib/guide-content";
// The parser comes from `inline-grammar`, not `guide-content`: `Inline` is rendered by
// client widgets, and `guide-content` reads the disk. A value import from there puts
// `node:fs` in the client bundle and the build fails outright. The block types above are
// `import type` and erase, so they are safe to keep pointing at the content model.
import { guideOutline } from "@/lib/guide-outline";
import { type GuideInline, parseInline } from "@/lib/inline-grammar";

function InlineRun({ run }: { run: GuideInline }) {
  if (run.type === "bold") {
    return <strong>{run.text}</strong>;
  }
  if (run.type === "italic") {
    return <em>{run.text}</em>;
  }
  if (run.type === "code") {
    return <code>{run.text}</code>;
  }
  if (run.type === "sup") {
    return <sup>{run.text}</sup>;
  }
  if (run.type === "sub") {
    return <sub>{run.text}</sub>;
  }
  if (run.type === "link") {
    return <Link href={run.href}>{run.text}</Link>;
  }
  return run.text;
}

/** Inline markup as React nodes. */
export function Inline({ text }: { text: string }) {
  return (
    <>
      {parseInline(text).map((run, index) => (
        <InlineRun
          key={`${index}-${run.type}-${run.text.slice(0, 24)}`}
          run={run}
        />
      ))}
    </>
  );
}

function renderInline(text: string) {
  return <Inline text={text} />;
}

function RowLabel({ label }: { label: string }) {
  return (
    <p className="mb-1 font-semibold text-foreground text-sm">
      <Inline text={label} />
    </p>
  );
}

/** The text of a list item, and the sub-list under it when there is one. */
function ListItems({
  items,
  ordered,
  className,
}: {
  items: GuideListItem[];
  ordered?: boolean;
  className?: string;
}) {
  const children = items.map((item) => {
    const text = typeof item === "string" ? item : item.text;
    return (
      <li key={text}>
        <Inline text={text} />
        {typeof item === "string" ? null : (
          <ListItems items={item.items} ordered={item.ordered} />
        )}
      </li>
    );
  });
  if (ordered) {
    return <ol className={className}>{children}</ol>;
  }
  return <ul className={className}>{children}</ul>;
}

function RowBody({ row }: { row: Extract<GuideExampleRow, { text: string }> }) {
  if (row.kind === "quote") {
    return (
      <blockquote className="border-border border-s-2 ps-4 text-foreground text-sm leading-relaxed">
        <Inline text={row.text} />
      </blockquote>
    );
  }
  // Preformatted rows are diagrams made of whitespace — a folder tree, an APA heading
  // hierarchy. `whitespace-pre` keeps them, and the scroller keeps them off the page.
  if (row.kind === "pre") {
    return (
      <pre className="overflow-x-auto whitespace-pre font-mono text-foreground text-sm leading-relaxed">
        {row.text}
      </pre>
    );
  }
  return (
    <p className="font-mono text-foreground text-sm leading-relaxed">
      <Inline text={row.text} />
    </p>
  );
}

function ExampleRow({ row }: { row: GuideExampleRow }) {
  return (
    <div className="mt-3 first:mt-0">
      {row.label ? <RowLabel label={row.label} /> : null}
      {"items" in row ? (
        <ListItems
          className={`space-y-1 ps-5 text-foreground text-sm ${row.ordered ? "list-decimal" : "list-disc"}`}
          items={row.items}
          ordered={row.ordered}
        />
      ) : (
        <RowBody row={row} />
      )}
    </div>
  );
}

/** A key that survives two identical rows in one box. */
function rowKey(row: GuideExampleRow, index: number): string {
  const body =
    "items" in row
      ? row.items.map((i) => (typeof i === "string" ? i : i.text)).join("|")
      : row.text;
  return `${index}-${row.label ?? ""}-${body.slice(0, 32)}`;
}

/** The bordered box the guides use for citation samples and checklists. */
function ExampleBlock({
  label,
  rows,
}: Extract<GuideBlock, { type: "example" }>) {
  return (
    <div className="not-prose my-6 rounded-lg border border-border bg-muted/30 p-4">
      {label ? (
        <p className="mb-2 font-semibold text-foreground text-sm">
          <Inline text={label} />
        </p>
      ) : null}
      {rows.map((row, index) => (
        <ExampleRow key={rowKey(row, index)} row={row} />
      ))}
    </div>
  );
}

function CalloutBlock({
  heading,
  body,
  ctaLinks,
}: Extract<GuideBlock, { type: "callout" }>) {
  return (
    <div className="not-prose my-10 rounded-lg border border-border bg-accent p-6">
      {heading ? (
        <h2 className="mb-4 font-semibold text-foreground text-lg">
          <Inline text={heading} />
        </h2>
      ) : null}
      {body ? (
        <p className="mb-4 text-muted-foreground text-sm leading-relaxed">
          <Inline text={body} />
        </p>
      ) : null}
      {ctaLinks?.length ? (
        <div className="flex flex-wrap gap-3">
          {ctaLinks.map((cta) => (
            <Link
              className="inline-block rounded-md bg-foreground px-6 py-2 font-medium text-background text-sm no-underline transition-opacity hover:opacity-90"
              href={cta.href}
              key={cta.href}
            >
              {cta.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function LinksBlock({
  heading,
  items,
  id,
}: Extract<GuideBlock, { type: "links" }> & { id?: string }) {
  return (
    <>
      <h2 className="scroll-mt-24" id={id}>
        {heading}
      </h2>
      <ul>
        {items.map((item) => (
          <li key={item.href}>
            <Link href={item.href}>{item.label}</Link>
          </li>
        ))}
      </ul>
    </>
  );
}

function ListBlock({
  type,
  items,
}: Extract<GuideBlock, { type: "ul" | "ol" }>) {
  return <ListItems items={items} ordered={type === "ol"} />;
}

/**
 * Side by side on a wide screen, stacked on a narrow one.
 *
 * The class names are looked up rather than interpolated: Tailwind reads the source for
 * literal class strings, and a template-built `md:grid-cols-${n}` is a class that never
 * gets generated and a comparison that silently renders as one column forever.
 */
const COLUMN_CLASS: Record<number, string> = {
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-2",
  6: "md:grid-cols-3",
};

function ColumnsBlock({ items }: Extract<GuideBlock, { type: "columns" }>) {
  return (
    <div
      className={`not-prose my-6 grid gap-4 ${COLUMN_CLASS[items.length] ?? "md:grid-cols-2"}`}
    >
      {items.map((column) => (
        <div
          className="rounded-lg border border-border bg-muted/30 p-4"
          key={column.label}
        >
          <p className="mb-2 font-semibold text-foreground text-sm">
            <Inline text={column.label} />
          </p>
          {column.rows.map((row, index) => (
            <ExampleRow key={rowKey(row, index)} row={row} />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * A comparison table.
 *
 * Wrapped in its own horizontal scroller: a four-column style comparison does not fit
 * a phone, and a table that overflows the `prose` container takes the whole page
 * sideways with it rather than scrolling on its own.
 */
function TableBlock({
  caption,
  head,
  rows,
  notes,
}: Extract<GuideBlock, { type: "table" }>) {
  return (
    <div className="not-prose my-6 overflow-x-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-left text-sm">
        {caption ? (
          <caption className="px-4 pt-3 text-left text-muted-foreground text-sm">
            <Inline text={caption} />
          </caption>
        ) : null}
        {head ? (
          <thead>
            <tr className="border-border border-b bg-muted/40">
              {head.map((cell) => (
                <th
                  className="px-4 py-2 font-semibold text-foreground"
                  key={cell}
                >
                  <Inline text={cell} />
                </th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {rows.map((row, index) => (
            <tr
              className="border-border/60 border-b last:border-0"
              key={`${index}-${row[0]}`}
            >
              {row.map((cell, cellIndex) => (
                <td
                  className="px-4 py-2 align-top text-muted-foreground"
                  key={`${cellIndex}-${cell}`}
                >
                  <Inline text={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {notes?.length ? (
        <div className="border-border border-t px-4 py-2 text-muted-foreground text-xs leading-relaxed">
          {notes.map((note) => (
            <p key={note}>
              <Inline text={note} />
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The visible FAQ. Its structured data is emitted by the route, from these same items.
 *
 * Google requires `FAQPage` markup to correspond to text the reader can see, and the
 * usual way a site breaks that is by keeping the two in separate arrays and editing
 * one. There is only one array here.
 */
function FaqBlock({
  heading,
  headingId,
  items,
}: Extract<GuideBlock, { type: "faq" }> & { headingId: string }) {
  // FaqList owns the heading element and puts the outline's id on it.
  return (
    <FaqList
      className="not-prose my-10"
      heading={<Inline text={heading} />}
      headingId={headingId}
      items={items.map((item) => ({
        answer: item.a,
        question: item.q,
      }))}
      renderText={renderInline}
    />
  );
}

/**
 * `h2` and `h3` carry the outline's id so the table of contents can link to them.
 * `scroll-mt-24` keeps the heading clear of the sticky site header when jumped to.
 */
function HeadingBlock({
  block,
  id,
}: {
  block: Extract<GuideBlock, { text: string }>;
  id?: string;
}) {
  const inner = <Inline text={block.text} />;
  if (block.type === "h2") {
    return (
      <h2 className="scroll-mt-24" id={id}>
        {inner}
      </h2>
    );
  }
  if (block.type === "h3") {
    return (
      <h3 className="scroll-mt-24" id={id}>
        {inner}
      </h3>
    );
  }
  if (block.type === "h4") {
    return <h4>{inner}</h4>;
  }
  if (block.type === "h5") {
    return <h5>{inner}</h5>;
  }
  return <p>{inner}</p>;
}

const TEXT_BLOCKS = new Set(["h2", "h3", "h4", "h5", "p"]);

function Block({
  block,
  index,
  id,
}: {
  block: GuideBlock;
  index: number;
  id?: string;
}) {
  if (TEXT_BLOCKS.has(block.type)) {
    return (
      <HeadingBlock
        block={block as Extract<GuideBlock, { text: string }>}
        id={id}
      />
    );
  }
  if (block.type === "ul" || block.type === "ol") {
    return <ListBlock {...block} />;
  }
  if (block.type === "example") {
    return <ExampleBlock {...block} />;
  }
  if (block.type === "callout") {
    return <CalloutBlock {...block} />;
  }
  if (block.type === "links") {
    return <LinksBlock {...block} id={id} />;
  }
  if (block.type === "table") {
    return <TableBlock {...block} />;
  }
  if (block.type === "faq") {
    return (
      <FaqBlock {...block} headingId={id ?? `guide-faq-${index}-heading`} />
    );
  }
  if (block.type === "columns") {
    return <ColumnsBlock {...block} />;
  }
  return null;
}

/** A key that survives two identical paragraphs in a row. */
function blockKey(block: GuideBlock, index: number): string {
  if ("text" in block) {
    return `${index}-${block.type}-${block.text.slice(0, 32)}`;
  }
  if ("heading" in block && block.heading) {
    return `${index}-${block.type}-${block.heading.slice(0, 32)}`;
  }
  return `${index}-${block.type}`;
}

export function GuideBody({ blocks }: { blocks: GuideBlock[] }) {
  // The same outline the table of contents is built from, so an anchor in the sidebar
  // can never point at an id the body did not render.
  const ids = new Map(
    guideOutline(blocks).map((heading) => [heading.index, heading.id])
  );
  return (
    <>
      {blocks.map((block, index) => (
        <Block
          block={block}
          id={ids.get(index)}
          index={index}
          key={blockKey(block, index)}
        />
      ))}
    </>
  );
}
